/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.triggerGeofence

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.triggerGeofence.internal.TriggerGeofencesUseCase
import features.triggerGeofence.internal.TriggerGeofencesUseCase.Params
import features.triggerGeofence.models.TriggerGeofenceResponseModel
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import features.triggerGeofence.models.TriggerGeofenceRequest

typealias TriggerGeofencesState = CFlow<TriggerGeofencesUseCaseResult?>

typealias TriggerGeofencesUseCaseResult = Result<TriggerGeofenceResponseModel>

fun TriggerGeofencesUseCaseResult.serializeTriggerGeofencesApiResult(): SerializedResult<String> {
    return this.toJsonResult<TriggerGeofenceResponseModel>()
}

/**
 * Serializes [TriggerGeofencesState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun TriggerGeofencesState.serializeTriggerGeofencesState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<TriggerGeofenceResponseModel>()
    }.asCFlow()
}

/**
 * Trigger a geofence event. [Api] class
 */
class TriggerGeofencesApi : Api() {

    val triggerGeofencesState: TriggerGeofencesState
        get() = _triggerGeofencesState.asCFlow()

    companion object {
        private val _triggerGeofencesState: MutableStateFlow<TriggerGeofencesUseCaseResult> = MutableStateFlow(Idle())
    }

    private val triggerGeofencesUseCase = TriggerGeofencesUseCase()

    /**
     * Trigger a geofence event.
     */
    suspend fun triggerGeofence(
        overrideHeaders: Map<String, String>? = null,
        triggerGeofenceRequest: TriggerGeofenceRequest
    ): Result<TriggerGeofenceResponseModel> {
        /* set state to Loading as soon as function is called */
        _triggerGeofencesState.value = Loading()
        val result = this.triggerGeofencesUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                triggerGeofenceRequest = triggerGeofenceRequest
            )
        ) { result ->
            _triggerGeofencesState.value = result
            return@triggerGeofencesUseCase result
        }
        return result
    }

    /**
    * Trigger a geofence event.
    *
    * This method is used by the Native artifact when dealing with object body params
    */
    suspend fun triggerGeofence(
        overrideHeaders: Map<String, String>? = null,
        methods: Map<String, Any>
    
    ): Result<TriggerGeofenceResponseModel> {
        return triggerGeofence(
            overrideHeaders = overrideHeaders,
            triggerGeofenceRequest = Json.decodeFromString(methods.getValue("triggerGeofenceRequest") as String)
        )
    }
}
